import * as React from 'react';
import { __, __r } from 'react-i18n';
import { rem } from 'polished';
import { round } from '../../../utilities';
import styled from 'styled-components';
import { DsiSvgIcon as SvgIcon, IconType } from '../General/SvgIcon';
import { getProductDefaultInfo } from '../../../utilities/selectors';
import { InfoIcon } from '../InfoIcon/InfoIcon';
import { DsiProductTabDefault as ProductTabDefault } from '../Product/ProductTabDefault';


export interface ComparisonAttribsDataItem {
    groupName: string;
    groupId: number;
    groupSort: number;
    attribs: {
      attribId: number;
      attribName: string;
      attribUnit: string;
      attribShortDescription: string;
      attribType: string;
      valueBestSort: number;
      attribSort: number;
      typeId: number;
      groupId: number;
      values: {
        productId: number;
        highlight: boolean;
        values: {
          name: string;
          link: string;
          shortDescription: string;
          numberRating: number;
        }[];
      }[];
    }[];
}

interface Props {
  products: any[];
  attribsData: ComparisonAttribsDataItem[];
  onProductRemove: (id: number) => void;
  addToCart: any;
  resetAll: () => void;
  shareMode: boolean;
}

export function Comparison({
  products,
  attribsData,
  onProductRemove,
  resetAll,
  addToCart,
  shareMode,
}: Props) {
  const columnRef = React.useRef<HTMLDivElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const [closedGroupsIds, setClosedGroupsIds] = React.useState([] as number[]);
  const [valuesOffsetX, setValuesOffsetX] = React.useState(0);
  const [slideIndex, setSlideIndex] = React.useState(0);

  const [valueInfoModalText, setValueInfoModalText] = React.useState<{
    title: string;
    text: string;
  } | null>(null);

  const onGroupOpenToggle = (groupId: number) => {
    if (closedGroupsIds.includes(groupId)) {
      setClosedGroupsIds(closedGroupsIds.filter(item => item !== groupId));
    } else {
      setClosedGroupsIds(closedGroupsIds.concat([groupId]));
    }
  };

  const onClickNext = () => {
    if (columnRef.current && containerRef.current) {
      const columnWidth = columnRef.current.offsetWidth;
      const containerWidth = containerRef.current.offsetWidth;

      const visibleItems = Math.floor(containerWidth / columnWidth);
      const maxIndex = products.length - visibleItems;

      setSlideIndex(prev => {
        if (prev >= maxIndex) return prev;
        setValuesOffsetX(-(prev + 1) * columnWidth);
        return prev + 1;
      });
    }
  };

  const onClickPrev = () => {
    if (columnRef?.current) {
      const columnWidth = columnRef.current.offsetWidth;

      setSlideIndex(prev => {
        if (prev <= 0) return 0; // Don't go before first item
        setValuesOffsetX(-(prev - 1) * columnWidth);
        return prev - 1;
      });
    }
  };

  return (
    <MainWrapper>
      <HeaderRow>
        <ParameterName></ParameterName>
        <BtnBackWrapper onClick={onClickPrev}>
          <BtnBack
            icon={IconType.arrowBack}
            width={24}
            height={24}
            fill={'#FFFFFF'}
          />
        </BtnBackWrapper>

        <BtnNextWrapper onClick={onClickNext}>
          <BtnNext
            icon={IconType.arrowNext}
            width={24}
            height={24}
            fill={'#FFFFFF'}
          />
        </BtnNextWrapper>

        <ValuesWrapper ref={containerRef as React.Ref<any>} offsetX={valuesOffsetX}>
          {products?.map((item, index) => {
            const {
              availid,
              availability,
              picture,
              pictureWebP,
              url,
              name,
              price,
              isNew,
              isSale,
              isFavorite,
              salePercentage,
              id,
              description,
              currency,
              variants,
              isSaleB2b,
              isSaleB2c,
              onStock,
              isSecondHand,
              isFreeDelivery,
              imagePath,
              oldPriceCorporate: oldPrice,
            } = getProductDefaultInfo(item, null, false, true);
            const salePercentageCalc = 100 - round((price / oldPrice) * 100, 2);

            return (
              <ParameterValuesHeader ref={index === 0 ? columnRef as React.Ref<any> : null}>
                <ProductTabDefaultWrapper>
                  <ProductTabDefaulCustom
                    id={id}
                    key={`p${id}`}
                    apiUrl={process.env.REACT_APP_API_BASE_URL as string}
                    picture={picture}
                    pictureWebP={pictureWebP}
                    price={price}
                    oldPrice={oldPrice}
                    availid={availid}
                    availability={availability}
                    url={url}
                    name={name}
                    addToCart={addToCart}
                    isNew={isNew}
                    isSale={isSaleB2b || isSaleB2c}
                    isFavorite={isFavorite}
                    salePercentage={salePercentageCalc.toString()}
                    product={item}
                    hasFavourite={false}
                    description={null}
                    currency={currency}
                    canOrder={true}
                    stockCount={0}
                    isSecondHand={isSecondHand}
                    isFreeDelivery={isFreeDelivery}
                    height={'initial'}
                    cannotOrderButtonText={__('Zobraziť')}
                    imagePath={imagePath}
                    boxShadow="initial"
                    showCloseIcon={true}
                    onClose={id => onProductRemove(id)}
                    displayType="small"
                  />
                </ProductTabDefaultWrapper>
              </ParameterValuesHeader>
            );
          })}
        </ValuesWrapper>
      </HeaderRow>
      <BottomPart>
        {attribsData?.map(attribDataItem => {
          return (
            <>
              {attribsData.length ? (
                <>
                  <GroupRow className="group">
                    <GroupParameterName
                      onClick={() => onGroupOpenToggle(attribDataItem.groupId)}
                    >
                      {' '}
                      <GroupNameContent>
                        {' '}
                        {attribDataItem.groupName}{' '}
                        {closedGroupsIds.includes(attribDataItem.groupId) ? (
                          <TriangleDown />
                        ) : (
                          <TriangleUp />
                        )}{' '}
                      </GroupNameContent>
                    </GroupParameterName>
                    <ValuesWrapper offsetX={valuesOffsetX}>
                      {products?.map(item => {
                        return <ParameterValues> </ParameterValues>;
                      })}
                    </ValuesWrapper>
                  </GroupRow>
                  <GroupMobileRow
                    className="group"
                    onClick={() => onGroupOpenToggle(attribDataItem.groupId)}
                  >
                    <GroupNameContent>
                      {' '}
                      {attribDataItem.groupName}{' '}
                      {closedGroupsIds.includes(attribDataItem.groupId) ? (
                        <TriangleDown />
                      ) : (
                        <TriangleUp />
                      )}{' '}
                    </GroupNameContent>
                  </GroupMobileRow>
                </>
              ) : (
                <></>
              )}

              {!closedGroupsIds.includes(attribDataItem.groupId) ? (
                attribDataItem.attribs.map(attrib => {
                  return (
                    <>
                      <MobileParameterName>
                        {' '}
                        {attrib.attribName}{' '}
                        <InfoIcon
                          infoDescription={attrib.attribShortDescription}
                        />
                      </MobileParameterName>
                      <Row className="param-row">
                        <ParameterName>
                          {' '}
                          <ParameterInfo>
                            {attrib.attribName}{' '}
                            <InfoIcon
                              infoDescription={attrib.attribShortDescription}
                            />
                          </ParameterInfo>{' '}
                        </ParameterName>
                        <ValuesWrapper offsetX={valuesOffsetX}>
                          {products?.map(item => {
                            const productAttrib = attrib.values.find(
                              value => value.productId === item.product_id,
                            );

                            if (productAttrib) {
                              return (
                                <ParameterValues
                                  highlighted={productAttrib.highlight}
                                >
                                  {' '}
                                  <BoxWrapper>
                                    {' '}
                                    {productAttrib.values.map(value => (
                                      <AttribValue>
                                        {' '}
                                        {`${value.name} ${attrib.attribUnit}`}{' '}
                                        <InfoIcon
                                          infoLink={value.link}
                                          infoDescription={
                                            value.shortDescription
                                          }
                                          onInfoClick={() => {
                                            return !value.link
                                              ? setValueInfoModalText({
                                                  title: value.name,
                                                  text: value.shortDescription,
                                                })
                                              : undefined;
                                          }}
                                        />
                                      </AttribValue>
                                    ))}{' '}
                                  </BoxWrapper>{' '}
                                </ParameterValues>
                              );
                            }
                            return <ParameterValues> -- </ParameterValues>;
                          })}
                        </ValuesWrapper>
                      </Row>
                    </>
                  );
                })
              ) : (
                <></>
              )}
            </>
          );
        })}
      </BottomPart>
      {/*
        {valueInfoModalText?.text ? (
            <InfoModal
            handleClose={() => {
                setValueInfoModalText(null);
            }}
            titleText={valueInfoModalText.title}
            bodyText={valueInfoModalText.text}
            enableCloseIcon={true}
            enableOutsideClick={true}
            />
        ) : (
            <></>
        )}
      */}
    </MainWrapper>
  );
}

const MainWrapper = styled.div`
  background: white;
  overflow-x: clip;
  max-width: 100%;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Row = styled.div`
  display: flex;
`;

const GroupRow = styled(Row)`
  background-color: ${({ theme }) => theme.colors.primary + '10'};

  ${({ theme }) => theme.mediab.l925`
      display: none;
  `}
`;

const GroupMobileRow = styled(Row)`
  background-color: ${({ theme }) => theme.colors.primary + '10'};
  display: none;
  padding-top: ${rem(10)};
  padding-bottom: ${rem(10)};
  padding-left: ${rem(10)};
  padding-right: ${rem(10)};

  ${({ theme }) => theme.mediab.l925`
    display: block;
  `}
`;

const HeaderRow = styled(Row)`
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 3;
`;

const TableBox = styled.div<{highlighted?: boolean}>`
  border-right: 1px solid #f3f3f3;
  padding-left: ${rem(8)};
  padding-right: ${rem(8)};
  padding-top: ${rem(8)};
  padding-bottom: ${rem(8)};
  ${({ highlighted }) => {
    return highlighted ? `background-color: rgba(54,191,145,0.08);` : ``;
  }}
`;

const ParameterName = styled(TableBox)`
  text-align: end;
  white-space: nowrap;
  width: ${rem(200)};
  min-width: ${rem(200)};
  background-color: white;
  z-index: 2;

  ${({ theme }) => theme.mediab.l925`
      display: none;
    `}
`;

const ParameterInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
`;

const GroupParameterName = styled(ParameterName)`
  background-color: initial;
`;

const ParameterValues = styled(TableBox)`
  flex: 0 0 33.33%;
  box-sizing: border-box;
  white-space: normal;

  ${({ theme }) => theme.mediab.l925`
       flex: 0 0 50%;
    `}
`;

const ParameterValuesHeader = styled(ParameterValues)`
  padding-left: 0;
  padding-right: 0;
  padding-top: 0;
  padding-bottom: 0;
`;

const ValuesWrapper = styled.div<{ offsetX?: number }>`
  display: flex;
  scroll-behavior: smooth;
  transform: ${({ offsetX = 0 }) => `translateX(${offsetX}px)`};
  transition: transform 0.3s ease;
  width: 100%;
  min-width: 0;
`;

const BoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductTabDefaulCustom = styled(ProductTabDefault)`
  height: 100% !important;
`;

const ProductTabDefaultWrapper = styled.div`
  height: 100%;
  width: 100%;
`;

const BottomPart = styled.div`
  & .param-row:nth-child(odd):not(.group) {
    background-color: #fbfbfb;
  }
`;

const AttribValue = styled.div`
  display: flex;
  align-items: center;
`;

const ValueInfoIconLink = styled.a``;

const ValueInfoIcon = styled.img`
  height: ${rem(16)};
  margin-left: ${rem(4)};
  filter: invert(1);
`;

const GroupNameContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${rem(20)};
  white-space: nowrap;
`;

const TriangleUp = styled.div`
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 8px solid black;
`;

const TriangleDown = styled.div`
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 8px solid black;
`;

const BtnBack = styled(SvgIcon)`
  opacity: 1;
  cursor: pointer;
  margin: auto;
`;

const BtnNext = styled(BtnBack)`
  opacity: 1;
`;

const BtnWrapper = styled.div`
  position: absolute;
  top: 50%;
  z-index: 6000;
  border-radius: 50%;
  background-color: #dadfed;
  padding: ${rem(5)};
`;

const BtnBackWrapper = styled(BtnWrapper)`
  left: ${rem(210)};

  ${({ theme }) => theme.mediab.l925`
       left: ${rem(10)};
    `}
`;

const BtnNextWrapper = styled(BtnWrapper)`
  right: ${rem(10)};
`;

const MobileParameterName = styled.div`
  display: none;
  padding-left: ${rem(8)};
  padding-top: ${rem(10)};
  font-weight: bold;

  ${({ theme }) => theme.mediab.l925`
    display: flex;
    align-items: center;
  `}
`;
